import { Button, Typography, formatPrice } from '@lucid/lego';
import { Quote } from 'src/api/services/lupp/quotes/dto.quotes';
import { formatDate } from '../../../../../../../utils/tools';
import styles from './utils.module.scss';

/**
 * Get translated payment method label
 * @param method - Payment method value from API (e.g., 'cash', 'loan', 'lease')
 * @param labels - i18n labels object
 * @returns Translated payment method string
 */
const getPaymentMethodLabel = (
  method: string | null | undefined,
  labels: Record<string, string>
): string => {
  if (!method) return '-';
  const key = `paymentMethod.${method.toLowerCase()}`;
  return labels[key] ?? method;
};

/**
 * Get translated status label
 * @param status - Status value from API (e.g., 'shared', 'draft', 'accepted')
 * @param labels - i18n labels object
 * @returns Translated status string
 */
const getStatusLabel = (
  status: string | null | undefined,
  labels: Record<string, string>
): string => {
  if (!status) return '-';
  const key = `quoteStatus.${status.toLowerCase().replace(/-/g, '_')}`;
  return labels[key] ?? formatStatus(status);
};

/**
 * Format status string for display by capitalizing each word
 * @param status - Status value with dashes (e.g., 'in-progress')
 * @returns Formatted status string (e.g., 'In Progress')
 * @example
 * ```ts
 * formatStatus('in-progress') // 'In Progress'
 * formatStatus('active') // 'Active'
 * ```
 */
const formatStatus = (status: string): string =>
  status
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Parameters for creating table columns configuration
 */
type CreateColumnsParams = {
  /** Theme for styling components */
  theme: 'light' | 'dark';
  /** Callback when a quote row is clicked */
  onQuoteClick: (record: Quote) => void;
  /** Callback when download button is clicked */
  onDownloadClick: (record: Quote) => void;
  /** Localized labels for column headers */
  labels: Record<string, string>;
  /** Whether a download is in progress */
  isDownloading?: boolean;
  /** ID of the quote currently being downloaded */
  downloadingQuoteId?: string | null;
  /** Locale for date/currency formatting */
  locale?: string;
};

/**
 * Create columns configuration for Quotes table
 * @param params - Column configuration parameters
 * @returns Array of column definitions
 */

/**
 * Format case/quote identifier to show last 12 characters in uppercase
 * @param caseId - Full identifier string (e.g., UUID)
 * @returns Last 12 characters in uppercase, or placeholder if null/undefined
 * @example
 * ```ts
 * formatCaseTitle('abc123-def456-789xyz') // '56-789XYZ' (last 12 chars)
 * formatCaseTitle(null) // 'xxxxxxxxxxxx'
 * ```
 */
export const formatCaseTitle = (caseId: string | null | undefined): string => {
  if (!caseId) return 'xxxxxxxxxxxx';
  return caseId.slice(-12).toUpperCase();
};

export const createColumns = ({
  theme,
  onQuoteClick,
  onDownloadClick,
  labels,
  isDownloading,
  downloadingQuoteId,
  locale = 'en-US',
}: CreateColumnsParams) => [
  {
    key: 'quote_identifier',
    title: labels.quote_id || 'Quote ID',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography
        variant="body"
        size="small"
        color="link"
        theme={theme}
        onClick={() => onQuoteClick(record)}
      >
        {formatCaseTitle(record?.quote_identifier)}
      </Typography>
    ),
  },
  {
    key: 'trim',
    title: labels.model_trim || 'Model/Trim',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.trim}
      </Typography>
    ),
  },
  {
    key: 'vin',
    title: labels.vin || 'VIN',
    width: '200px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.vin}
      </Typography>
    ),
  },
  {
    key: 'total_amount',
    title: labels.total_amount || 'Total Amount',
    width: '120px',
    align: 'right' as const,
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.total_amount ? formatPrice(Number(record.total_amount), { locale }) : '-'}
      </Typography>
    ),
  },
  {
    key: 'preferredPaymentMethod',
    title: labels.payment_method || 'Payment Method',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {getPaymentMethodLabel(record.preferredPaymentMethod, labels)}
      </Typography>
    ),
  },
  {
    key: 'expiry_date',
    title: labels.expiry_date || 'Expiry Date',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record?.expiry_date ? formatDate(record?.expiry_date, locale) : 'N/A'}
      </Typography>
    ),
  },
  {
    key: 'status',
    title: labels.status || 'Status',
    width: '130px',
    sortable: true,
    render: (_: unknown, record: Quote) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {getStatusLabel(record.status, labels)}
      </Typography>
    ),
  },
  {
    key: 'action',
    title: labels.action || 'Action',
    width: '120px',
    align: 'center' as const,
    sortable: false,
    render: (_: unknown, record: Quote) => {
      const isCurrentDownloading = isDownloading && downloadingQuoteId === record.quote_identifier;
      const normalizedStatus = record.status?.toLowerCase();
      const isDownloadDisabledByStatus =
        normalizedStatus === 'accepted' || normalizedStatus === 'expired';
      return (
        <Button
          variant="button"
          type="secondary"
          color="neutral"
          size="small"
          label={
            isCurrentDownloading
              ? labels.downloading || 'Downloading...'
              : labels.download || 'Download'
          }
          handleClick={() => onDownloadClick(record)}
          gaComponent="quotes-download"
          dataTestId={`quote-download-${record.quote_identifier}`}
          theme={theme === 'dark' ? 'light' : 'dark'}
          disabled={isCurrentDownloading || isDownloadDisabledByStatus}
          className={styles.downloadButton}
        />
      );
    },
  },
];
